import { Button } from "@/components/ui/button";
import { Settings, ChevronDown, LogOut, LayoutDashboard, CreditCard, Users as UsersIcon, BookOpen, Home, ShoppingCart, Star, Calendar, MessageSquare, User, CreditCard as BillingIcon, Shield, GraduationCap, ShoppingBag, ScanLine } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import logoFull from "@/assets/logo-full.png";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const { user, signOut, hasRole, hasPlan, isPtaAdmin, subscription } = useAuth();
  const { isTrialExpired, loading: trialLoading } = useTrialStatus();
  const [hasPta, setHasPta] = useState(false);
  const navigate = useNavigate();

  // Check if user has a PTA
  const { data: userPtaData } = useQuery({
    queryKey: ["userPta", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      // First try by user_id
      let { data } = await supabase
        .from("members")
        .select("pta_id")
        .eq("user_id", user.id)
        .maybeSingle();
      
      // If not found by user_id, try by email
      if (!data && user.email) {
        const { data: emailData } = await supabase
          .from("members")
          .select("pta_id, id")
          .eq("email", user.email)
          .maybeSingle();
        
        // If found by email, link it
        if (emailData?.pta_id) {
          await supabase
            .from("members")
            .update({ user_id: user.id })
            .eq("id", emailData.id);
          
          data = emailData;
        }
      }
      
      setHasPta(!!data?.pta_id);
      return data;
    },
    enabled: !!user?.id,
  });

  // Check if user can access check-in (events role + standard plan)
  const canAccessCheckin = hasPta && (hasRole("events") || hasRole("site_admin")) && hasPlan("standard");

  // Fetch user profile for avatar
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("avatar_url, display_name, full_name")
        .eq("id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.substring(0, 2).toUpperCase();
  };
  
  const getUserName = () => {
    return profile?.display_name || profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "User";
  };

  // Define dashboard configurations with role and plan requirements
  const dashboards = [
    { path: "/dashboard/chair", label: "Chair Dashboard", roles: ["site_admin", "chair"], plan: null, icon: UsersIcon },
    { path: "/dashboard/treasurer", label: "Treasurer Dashboard", roles: ["site_admin", "treasurer"], plan: null, icon: CreditCard },
    { path: "/dashboard/secretary", label: "Secretary Dashboard", roles: ["site_admin", "secretary"], plan: null, icon: BookOpen },
    { path: "/dashboard/trustee", label: "Trustee Dashboard", roles: ["site_admin", "trustee"], plan: null, icon: Shield },
    { path: "/dashboard/assets", label: "Assets Dashboard", roles: ["site_admin", "assets"], plan: "standard" as const, icon: Home },
    { path: "/dashboard/catering", label: "Catering Dashboard", roles: ["site_admin", "catering"], plan: "standard" as const, icon: ShoppingCart },
    { path: "/dashboard/events", label: "Events Dashboard", roles: ["site_admin", "events"], plan: null, icon: Calendar },
    { path: "/dashboard/communications", label: "Communications Dashboard", roles: ["site_admin", "communications"], plan: "standard" as const, icon: MessageSquare },
    { path: "/dashboard/community", label: "Community Dashboard", roles: ["site_admin", "community"], plan: "standard" as const, icon: Star },
    { path: "/dashboard/school-liaison", label: "School Liaison Dashboard", roles: ["site_admin", "school_liaison"], plan: "standard" as const, icon: GraduationCap },
    { path: "/dashboard/shop", label: "Shop Dashboard", roles: ["site_admin", "shop"], plan: "standard" as const, icon: ShoppingBag },
  ];

  // Filter dashboards based on user roles and trial status
  // When trial is expired, hide all role-based dashboards (users must subscribe first)
  // Site admins always see everything
  const availableDashboards = user ? dashboards.filter(dashboard => {
    // Site admins see everything regardless of trial status
    if (hasRole("site_admin")) return true;
    
    // Hide dashboards while trial status is loading to prevent flash
    if (trialLoading) return false;
    
    // If trial expired, don't show any role-based dashboards
    if (isTrialExpired) return false;
    
    // Others need the specific role to see the dashboard
    const hasRequiredRole = dashboard.roles.some(role => hasRole(role));
    return hasRequiredRole;
  }) : [];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="container flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={logoFull} alt="PTA Hub by Wye Commerce" className="h-16" />
        </Link>

        <nav className="hidden md:flex items-center space-x-6">
          {!user && (
            <>
              <Link to="/">
                <Button variant="ghost">Home</Button>
              </Link>
              <Link to="/features">
                <Button variant="ghost">Features</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="ghost">Pricing</Button>
              </Link>
              <Link to="/about">
                <Button variant="ghost">About</Button>
              </Link>
            </>
          )}
          
          {user && (hasPta || isPtaAdmin) && (
            <>
              <Link to="/dashboard">
                <Button variant="ghost">Home</Button>
              </Link>
              {!trialLoading && !isTrialExpired && (
                <>
                  <Link to="/shop">
                    <Button variant="ghost">Shop</Button>
                  </Link>
                  <Link to="/forum">
                    <Button variant="ghost">Forum</Button>
                  </Link>
                  <Link to="/library">
                    <Button variant="ghost">Library</Button>
                  </Link>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-1">
                    My Dashboards
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-popover z-50">
                  {hasPta && !trialLoading && !isTrialExpired && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-3 cursor-pointer">
                        <LayoutDashboard className="h-4 w-4" />
                        <span>Main Dashboard</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {hasPta && !trialLoading && !isTrialExpired && availableDashboards.length > 0 && <DropdownMenuSeparator />}
                  {availableDashboards.map((dashboard) => {
                    const Icon = dashboard.icon;
                    return (
                      <DropdownMenuItem key={dashboard.path} asChild>
                        <Link to={dashboard.path} className="flex items-center gap-3 cursor-pointer">
                          <Icon className="h-4 w-4" />
                          <span>{dashboard.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  {isPtaAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/pta-admin" className="flex items-center gap-3 cursor-pointer">
                          <Settings className="h-4 w-4" />
                          <span>PTA Admin</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </nav>

        <div className="flex items-center space-x-2">
          {user && !hasPta && (
            <Link to="/onboarding">
              <Button>PTA Setup</Button>
            </Link>
          )}
          {/* Event Check-in Icon - only for standard plan with events role */}
          {user && canAccessCheckin && (
            <div className="lg:hidden">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate("/event-checkin")}
                      className="h-9 w-9"
                    >
                      <ScanLine className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Event Check-in</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-auto py-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url} />
                    <AvatarFallback className="text-xs">{getUserInitials()}</AvatarFallback>
                  </Avatar>
                  <span className="hidden lg:inline-block">{getUserName()}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover z-50">
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=profile" className="flex items-center gap-3 cursor-pointer">
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                {isPtaAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/pta-admin" className="flex items-center gap-3 cursor-pointer">
                        <Settings className="h-4 w-4" />
                        <span>PTA Admin</span>
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                {hasRole("site_admin") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/site-admin" className="flex items-center gap-3 cursor-pointer">
                        <Shield className="h-4 w-4" />
                        <span>Site Admin</span>
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="flex items-center gap-3 cursor-pointer">
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth">
              <Button>Sign In</Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
